"use client";

import { useId, useState, useCallback, useEffect } from "react";
import clsx from "clsx";

function validateField(
  value: string,
  opts: { required?: boolean; minLength?: number; maxLength?: number; type?: string }
): string | null {
  if (opts.required && !value.trim()) return "This field is required.";
  if (value.trim() && opts.minLength && value.length < opts.minLength) {
    if (opts.maxLength && opts.minLength === opts.maxLength)
      return `Must be exactly ${opts.minLength} characters.`;
    return `Must be at least ${opts.minLength} characters.`;
  }
  if (value.trim() && opts.maxLength && value.length > opts.maxLength)
    return `Must be ${opts.maxLength} characters or less.`;
  if (value.trim() && opts.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
    return "Please enter a valid email address.";
  return null;
}

interface AuthInputProps {
  id?: string;
  name: string;
  type: "text" | "email" | "password";
  label: string;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  autoComplete?: string;
  inputMode?: "text" | "numeric" | "email" | "tel" | "search" | "url" | "none" | "decimal";
  pattern?: string;
  errors?: string[];
  defaultValue?: string;
  value?: string;
  disabled?: boolean;
  labelClassName?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  filterInput?: (value: string) => string;
}

export default function AuthInput({
  id,
  name,
  type,
  label,
  placeholder,
  required = false,
  minLength,
  maxLength,
  autoComplete,
  inputMode,
  pattern,
  errors,
  defaultValue,
  value,
  disabled = false,
  labelClassName,
  onChange,
  filterInput,
}: AuthInputProps) {
  const generatedId = useId();
  const inputId = id || `${name}-${generatedId}`;
  const hasServerErrors = errors && errors.length > 0;

  const [blurError, setBlurError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const displayErrors = hasServerErrors
    ? errors
    : touched && blurError
      ? [blurError]
      : [];

  const hasErrors = displayErrors.length > 0;

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setTouched(true);
    const err = validateField(e.target.value, { required, minLength, maxLength, type });
    setBlurError(err);
  }, [required, minLength, maxLength, type]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (filterInput) {
      const filtered = filterInput(e.target.value);
      if (filtered !== e.target.value) {
        e.target.value = filtered;
      }
    }
    if (touched && blurError) {
      const err = validateField(e.target.value, { required, minLength, maxLength, type });
      setBlurError(err);
    }
    onChange?.(e);
  }, [onChange, touched, blurError, required, minLength, maxLength, type, filterInput]);

  useEffect(() => {
    if (hasServerErrors) setTouched(false); // eslint-disable-line react-hooks/set-state-in-effect -- reset validation state when server errors arrive
  }, [hasServerErrors]);

  return (
    <div className="relative flex flex-col gap-2 pb-6">
      <label
        htmlFor={inputId}
        className={clsx("text-sm lg:text-base font-semibold flex items-center", labelClassName || "text-(--purple)")}
      >
        {label}
        {required && <span className="text-(--green) ml-1">*</span>}
      </label>
      <input
        id={inputId}
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
        minLength={minLength}
        maxLength={maxLength}
        autoComplete={autoComplete}
        inputMode={inputMode}
        pattern={pattern}
        {...(value !== undefined ? { value } : { defaultValue })}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-invalid={hasErrors}
        aria-describedby={hasErrors ? `${inputId}-errors` : undefined}
        className={clsx(
          "w-full rounded-lg px-4 py-3 text-base",
          "bg-(--bg-card) border-2 transition-colors",
          "text-(--text-primary) placeholder:text-(--text-muted)",
          "focus:outline-none focus:ring-2 focus:ring-(--green)/50",
          hasErrors
            ? "border-(--status-danger) focus:border-(--status-danger)"
            : "border-(--border-color-strong) focus:border-(--green)",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      />
      <div className="absolute bottom-0 left-0 right-0">
        {hasErrors && (
          <div id={`${inputId}-errors`} className="flex flex-col gap-1" role="alert">
            {displayErrors.map((error, i) => (
              <p key={i} className="text-(--text-error) text-sm">
                {error}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
